import Moveable from "moveable";

import { COMPOSITION_CONTAINER_ID } from "../../../config/config";

interface InteractiveElementListeners {
    dragStart?: () => void;
    scaleStart?: () => void;
    resizeStart?: () => void;
    rotateStart?: () => void;
    onRotate?: (rotation: number) => void;
    onDrag?: (x: number, y: number) => void;
    onScale?: (deltaWidth: number, deltaHeight: number) => void;
    onResize?: (deltaWidth: number, deltaHeight: number) => void;
    dragEnd?: () => void;
    rotateEnd?: () => void;
    resizeEnd?: () => void;
    scaleEnd?: () => void;
    onUpdate?: () => void;
}

interface HandlersOptions {
    showRotation?: boolean;
    resizable?: boolean;
    scalable?: boolean;
    keepRatio?: boolean;
}

export class InteractiveElement {
    private moveable: Moveable | null = null;
    private element: HTMLElement;
    private listeners?: InteractiveElementListeners;

    private getCompositionContainer(): HTMLElement | null {
        return document.getElementById(COMPOSITION_CONTAINER_ID) as HTMLElement | null;
    }

    /**
     * Defensive cleanup: in dev / fast remounts we've seen Moveable control boxes linger in the DOM.
     * Hide or remove any orphaned control boxes under the composition container.
     */
    private cleanupOrphanedControlBoxes(options: { keep?: HTMLElement | null; remove?: boolean } = {}) {
        const container = this.getCompositionContainer();
        if (!container) return;

        const keep = options.keep ?? null;
        const shouldRemove = Boolean(options.remove);

        try {
            // Only operate on top-level control boxes under the composition container.
            // Moveable can render nested boxes (e.g. internal managers); touching those can break visibility.
            const boxes = container.querySelectorAll<HTMLElement>(":scope > .moveable-control-box");
            for (const box of Array.from(boxes)) {
                if (keep && box === keep) continue;
                if (shouldRemove) box.remove();
                else box.style.display = "none";
            }
        } catch {}
    }

    constructor(element: HTMLElement, listeners?: InteractiveElementListeners) {
        this.element = element;
        this.listeners = listeners;

        this.init();
    }

    init() {
        const compositionContainer = this.getCompositionContainer();

        if (!compositionContainer) {
            return;
        }

        // Clear any lingering Moveable DOM from previous mounts (hot reload / React strict mode).
        this.cleanupOrphanedControlBoxes({ remove: true });

        this.moveable = new Moveable(compositionContainer, {
            // Start with no target; we'll set target when selected, and clear it when deselected.
            // This prevents Moveable from leaving a stale control box visible after deselect.
            target: null,
            className: "visibility-hidden-important",
            draggable: true,
            resizable: true,
            rotatable: false,
            keepRatio: true,
            scalable: true,
            throttleResize: 0,
            throttleDrag: 0,
            throttleRotate: 0,
            throttleScale: 0,
            origin: false,
        });

        // Ensure no flash of the control box before our first showHandlers call.
        try {
            // @ts-ignore
            if (this.moveable.controlBox) this.moveable.controlBox.style.display = "none";
        } catch {}

        this.listeners?.onUpdate?.();

        this.moveable.on("drag", (event) => {
            const {
                translate: [x, y],
            } = event;

            this.listeners?.onDrag?.(x, y);
            this.listeners?.onUpdate?.();
        });

        this.moveable.on("resize", (event) => {
            const [deltaWidth, deltaHeight] = event.delta;
            const [directionX, directionY] = event.direction;
            // directionX;

            if (directionY != 0 && this.listeners?.onScale != null) {
                this.listeners?.onScale?.(deltaWidth, deltaHeight);
            } else {
                this.listeners?.onResize?.(deltaWidth, deltaHeight);
            }

            this.listeners?.onUpdate?.();
        });

        this.moveable.on("rotate", (event) => {
            const rotation = event.rotation * (Math.PI / 180);

            this.listeners?.onRotate?.(rotation);
            this.listeners?.onUpdate?.();
        });

        this.moveable.on("dragStart", () => this.listeners?.dragStart?.());

        this.moveable.on("scaleStart", () => this.listeners?.scaleStart?.());

        this.moveable.on("resizeStart", () => this.listeners?.resizeStart?.());

        this.moveable.on("rotateStart", () => this.listeners?.rotateStart?.());

        this.moveable.on("dragEnd", () => this.listeners?.dragEnd?.());
        this.moveable.on("scaleEnd", () => this.listeners?.scaleEnd?.());
        this.moveable.on("rotateEnd", () => this.listeners?.rotateEnd?.());
        this.moveable.on("resizeEnd", () => this.listeners?.resizeEnd?.());
    }

    private parseHandlersOptions(options: HandlersOptions): Required<HandlersOptions> {
        return {
            showRotation: options.showRotation ?? true,
            resizable: options.resizable ?? true,
            scalable: options.scalable ?? true,
            keepRatio: options.keepRatio ?? true,
        };
    }

    showHandlers(options: HandlersOptions = {}) {
        if (this.moveable == null) this.init();
        if (this.moveable == null) return;

        // Rotation is controlled via CanvasClipToolbar (90° steps); disable rotation handle here.
        const parsed = this.parseHandlersOptions(options);
        try {
            // Important: use setState so the internal React control box updates immediately.
            this.moveable.setState({
                target: this.element,
                className: "visibility-visible-important",
                rotatable: false,
                resizable: parsed.resizable,
                scalable: parsed.scalable,
                keepRatio: parsed.keepRatio,
            });
            this.moveable.forceUpdate?.();
        } catch {}

        this.element.style.display = "block";
        try { this.moveable.updateRect(); } catch {}
    }

    hideHandlers() {
        if (this.moveable == null) this.init();
        if (this.moveable == null) return;

        try {
            // Critical: clear `target` so Moveable stops rendering its control box.
            this.moveable.setState({
                target: null,
                className: "visibility-hidden-important",
            });
            this.moveable.forceUpdate?.();
        } catch {}

        this.element.style.display = "none";
    }

    destroy() {
        const moveable: any = this.moveable as any;
        const controlBox: HTMLElement | null = moveable?.controlBox ?? null;
        try {
            this.moveable?.destroy();
        } catch {}
        // Defensive cleanup: Moveable should remove its DOM, but we've seen stale boxes linger.
        try {
            if (controlBox && controlBox.parentElement) controlBox.parentElement.removeChild(controlBox);
        } catch {}
        // Clear any other orphaned boxes as a last resort.
        this.cleanupOrphanedControlBoxes({ remove: true });
        this.moveable = null;
    }

    updateStyle(style: Partial<CSSStyleDeclaration>) {
        Object.assign(this.element.style, style);

        if (this.moveable === null) return;

        this.moveable?.updateRect();
    }
}
